import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Edit, Trash2, Play, Pause, FileText, Database,
  AlertTriangle, Plus, Sparkles, X, ListChecks, Target,
  ChevronDown, ChevronUp, User, CheckCircle, Loader2, Radio,
  FolderOpen, Upload, Workflow, ExternalLink, RefreshCw, Unlink,
} from 'lucide-react';
import { Badge } from '@/components/common/Badge';
import { Modal } from '@/components/common/Modal';
import { AgentTestChat } from '@/components/agents/AgentTestChat';
import { FileUploadZone } from '@/components/agents/FileUploadZone';
import { useAgentStore } from '@/store/agentStore';

import { skillsService } from '@/services/skills.service';
import { knowledgeService, KnowledgeBase, KnowledgeDocument } from '@/services/knowledge.service';
import { channelsService, Channel } from '@/services/channels.service';
import { taskPipelineService } from '@/services/taskPipeline.service';
import { integrationsService, Integration } from '@/services/integrations.service';
import { EscalationRule, NotificationChannelConfig, Skill, AgentTask, AgentTaskType, TaskReferenceTemplate, NikaidoWorkflow, NikaidoTriggerContract } from '@/types';
import { notificationsService } from '@/services/notifications.service';
import { nikaidoService } from '@/services/nikaido.service';
import toast from 'react-hot-toast';
import { cn } from '@/utils/cn';
import { MarkdownEditor } from '@/components/common/MarkdownEditor';
import { InfoTooltip } from '@/components/common/Tooltip';
// ReactMarkdown available if needed for rich content rendering
// import ReactMarkdown from 'react-markdown';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const INPUT_CLASS =
  'w-full h-10 px-3 bg-white border-2 border-[#1a1a2e] rounded-xl text-sm text-gray-900 focus:ring-2 focus:ring-[#1a1a2e]/20 focus:shadow-[3px_3px_0px_#1a1a2e] outline-none transition-colors';
const TEXTAREA_CLASS =
  'w-full px-3 py-2.5 bg-white border-2 border-[#1a1a2e] rounded-xl text-sm text-gray-900 focus:ring-2 focus:ring-[#1a1a2e]/20 focus:shadow-[3px_3px_0px_#1a1a2e] outline-none transition-colors min-h-[140px] resize-none';

// ---------------------------------------------------------------------------
// StatusBadge
// ---------------------------------------------------------------------------
const StatusBadge = ({ status }: { status: string }) => {
  const config: Record<string, { dot: string; label: string; bg: string; text: string }> = {
    active: { dot: 'bg-emerald-500', label: 'Live', bg: 'bg-emerald-50', text: 'text-emerald-700' },
    testing: { dot: 'bg-amber-500', label: 'Testing', bg: 'bg-amber-50', text: 'text-amber-700' },
    paused: { dot: 'bg-orange-400', label: 'Paused', bg: 'bg-orange-50', text: 'text-orange-700' },
    draft: { dot: 'bg-gray-300', label: 'Draft', bg: 'bg-gray-50', text: 'text-gray-600' },
    archived: { dot: 'bg-red-400', label: 'Archived', bg: 'bg-red-50', text: 'text-red-700' },
  };
  const c = config[status] || config.draft;
  return (
    <span className={cn('inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium', c.bg, c.text)}>
      <span className={cn('w-1.5 h-1.5 rounded-full', c.dot)} />
      {c.label}
    </span>
  );
};

// ---------------------------------------------------------------------------
// SectionCard
// ---------------------------------------------------------------------------
export const SectionCard: React.FC<{
  title: string;
  icon: React.ReactNode;
  iconBg: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, icon, iconBg, action, children }) => (
  <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] overflow-hidden">
    <div className="flex items-center justify-between px-6 py-4 border-b-2 border-[#1a1a2e]">
      <div className="flex items-center gap-3">
        <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center', iconBg)}>
          {icon}
        </div>
        <h3 className="text-[13px] font-semibold text-gray-900">{title}</h3>
      </div>
      {action}
    </div>
    <div className="px-6 py-5">{children}</div>
  </div>
);

// ---------------------------------------------------------------------------
// ReadOnlyField
// ---------------------------------------------------------------------------
const ReadOnlyField: React.FC<{
  label: string;
  value: string;
  onEdit: () => void;
  placeholder?: string;
}> = ({ label, value, onEdit, placeholder }) => (
  <div className="group">
    <div className="flex items-center justify-between mb-1">
      <span className="text-xs text-gray-400">{label}</span>
      <button onClick={onEdit} className="flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-medium text-violet-600 bg-violet-50 hover:bg-violet-100 border border-violet-200 transition-all">
        <Edit className="w-3 h-3" /> Edit
      </button>
    </div>
    <p className={cn('text-sm leading-relaxed break-words overflow-hidden', value ? 'text-gray-700' : 'text-gray-300 italic')}>
      {value || placeholder || 'Not set'}
    </p>
  </div>
);

// ---------------------------------------------------------------------------
// Channel emoji helper
// ---------------------------------------------------------------------------
const getChannelEmoji = (type: string) => {
  const map: Record<string, string> = {
    web: '\uD83C\uDF10',
    telegram: '\u2708\uFE0F',
    whatsapp: '\uD83D\uDCAC',
    facebook: '\uD83D\uDC64',
    instagram: '\uD83D\uDCF7',
    email: '\uD83D\uDCE7',
    sms: '\uD83D\uDCF1',
    slack: '\uD83D\uDCBC',
    custom: '\uD83D\uDD17',
  };
  return map[type] || '\uD83D\uDCAC';
};

// ---------------------------------------------------------------------------
// Pipeline field helpers
// ---------------------------------------------------------------------------
const SUGGESTED_FIELDS = [
  { label: 'Name', key: 'full_name', type: 'text', prompt: 'What is your name?' },
  { label: 'Email', key: 'email', type: 'email', prompt: 'What is your email address?' },
  { label: 'Phone', key: 'phone', type: 'phone', prompt: 'What is your phone number?' },
  { label: 'Company', key: 'company', type: 'text', prompt: 'What company do you work for?' },
  { label: 'Address', key: 'address', type: 'text', prompt: 'What is your address?' },
  { label: 'Budget', key: 'budget', type: 'number', prompt: 'What is your budget?' },
  { label: 'Website', key: 'website', type: 'text', prompt: 'What is your website?' },
];

const detectFieldType = (label: string): string => {
  const l = label.toLowerCase();
  if (l.includes('email')) return 'email';
  if (l.includes('phone') || l.includes('mobile')) return 'phone';
  if (l.includes('date') || l.includes('birthday') || l.includes('dob')) return 'date';
  if (l.includes('budget') || l.includes('price') || l.includes('amount') || l.includes('salary') || l.includes('age') || l.includes('count') || l.includes('size') || l.includes('number')) return 'number';
  return 'text';
};

const generateKey = (label: string): string =>
  label.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim().replace(/\s+/g, '_');

const generatePrompt = (label: string): string =>
  `What is your ${label.toLowerCase()}?`;


// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export const AgentDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { selectedAgent, fetchAgent, deleteAgent, updateAgent, updateAgentStatus, connectWorkflow, disconnectWorkflow, updateWorkflowConfig } = useAgentStore();

  // Dirty / save state
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const initialLoadDone = useRef(false);

  // Plan 011 — the legacy per-agent Nikaido workflow selector is hidden. Agents now
  // use the shared active-conversation workflow by default; the block below stays in
  // the tree (behind this flag) so its handlers remain available for a later cleanup.
  const SHOW_LEGACY_WORKFLOW_UI = false;
  const [savingActiveConv, setSavingActiveConv] = useState(false);

  // Popup state
  const [activePopup, setActivePopup] = useState<string | null>(null);

  // Expanded section for inline editing
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  // Knowledge base state
  const [agentDocuments, setAgentDocuments] = useState<(KnowledgeDocument & { knowledge_base_name?: string })[]>([]);
  const [kbPendingFiles, setKbPendingFiles] = useState<File[]>([]);
  const [isUploadingKb, setIsUploadingKb] = useState(false);
  const [allKnowledgeBases, setAllKnowledgeBases] = useState<KnowledgeBase[]>([]);
  const [isLoadingKbs, setIsLoadingKbs] = useState(false);
  const [togglingKbId, setTogglingKbId] = useState<string | null>(null);
  const [expandedKbId, setExpandedKbId] = useState<string | null>(null);
  const [kbDocuments, setKbDocuments] = useState<Record<string, KnowledgeDocument[]>>({});
  const [loadingKbDocs, setLoadingKbDocs] = useState<string | null>(null);
  const [knowledgeTab, setKnowledgeTab] = useState<'browse' | 'upload'>('browse');

  // Channels
  const [orgChannels, setOrgChannels] = useState<Channel[]>([]);

  // Nikaido workflows
  const [nikaidoWorkflows, setNikaidoWorkflows] = useState<NikaidoWorkflow[]>([]);
  const [isLoadingWorkflows, setIsLoadingWorkflows] = useState(false);
  const [isWorkflowBusy, setIsWorkflowBusy] = useState(false);
  const [newWorkflowName, setNewWorkflowName] = useState('');
  const [triggerContract, setTriggerContract] = useState<NikaidoTriggerContract | null>(null);
  const [isLoadingTriggerContract, setIsLoadingTriggerContract] = useState(false);
  const [isSavingCredentialBindings, setIsSavingCredentialBindings] = useState(false);
  const [credentialBindings, setCredentialBindings] = useState<Record<string, { integration_id: string }>>({});

  // Pipeline
  const [pipelineTasks, setPipelineTasks] = useState<AgentTask[]>([]);
  const [pipelineWizardStep, setPipelineWizardStep] = useState<'choose' | 'configure'>('choose');
  const [pipelineForm, setPipelineForm] = useState({
    name: '',
    task_type: 'data_collection' as AgentTaskType,
    description: '',
    fields: [] as { label: string; key: string; type: string; required: boolean; prompt: string }[],
    document_guidance: '',
    reference_template_id: '' as string,
    output_name_template: '' as string,
    depends_on: '' as string,
    workflow_name: '',
    destination_type: 'internal' as string,
    webhook_url: '',
    destination_webhook: false,
    destination_webhook_url: '',
    destination_integrations: [] as string[],
    notification_enabled: false,
    notification_channel_ids: '',
    notification_priority: 'normal' as 'low' | 'normal' | 'high' | 'urgent',
    business_hours_enabled: false,
    business_hours_timezone: 'UTC',
    business_hours_schedule: {} as Record<string, { open: string; close: string } | null>,
    require_confirmation: true,
    action_type: 'submission' as 'order' | 'submission',
  });
  const [editingPipelineTask, setEditingPipelineTask] = useState<AgentTask | null>(null);
  const [customFieldInput, setCustomFieldInput] = useState('');
  const [referenceTemplates, setReferenceTemplates] = useState<TaskReferenceTemplate[]>([]);
  const [availableIntegrations, setAvailableIntegrations] = useState<Integration[]>([]);

  // Escalation rules
  const [orgNotifChannels, setOrgNotifChannels] = useState<NotificationChannelConfig[]>([]);
  const [editingEscalation, setEditingEscalation] = useState<EscalationRule | null>(null);
  const [escalationForm, setEscalationForm] = useState({
    name: '',
    priority: 'normal' as 'low' | 'normal' | 'high' | 'urgent',
    guideline: '',
    notification_message: '',
    notification_channel_ids: [] as string[],
  });

  // Skills
  const [agentSkills, setAgentSkills] = useState<Skill[]>([]);
  const [allSkills, setAllSkills] = useState<Skill[]>([]);

  // Create skill form
  const [newSkillForm, setNewSkillForm] = useState({
    name: '',
    description: '',
    skill_type: 'topic' as 'topic' | 'common',
    rules: [''],
    examples: [] as { customer: string; agent: string }[],
  });

  // Configuration form state
  const [configForm, setConfigForm] = useState({
    name: '',
    description: '',
    custom_instructions: '',
    greeting_message: '',
    goodbye_message: '',
    tone: 'professional' as 'professional' | 'friendly' | 'casual',
    language: 'en',
    ai_provider: 'gemini' as string,
    ai_model: 'gemini-2.5-flash' as string,
    ai_temperature: 0.7,
    ai_max_tokens: 1024,
    lead_qualification_enabled: false,
    lead_qualification_criteria: '',
    lead_scoring_enabled: false,
    lead_scoring_high: '',
    lead_scoring_good: '',
    lead_scoring_medium: '',
    lead_scoring_low: '',
    lead_scoring_minimal: '',
  });

  // ------------------------------------------------------------------
  // Data loading on mount
  // ------------------------------------------------------------------
  useEffect(() => {
    if (id) {
      fetchAgent(id);

      loadDocuments();
      loadSharedKnowledgeBases();
      skillsService.getAgentSkills(id).then(setAgentSkills).catch(() => {});
      skillsService.getSkills().then(setAllSkills).catch(() => {});
      channelsService.getChannels().then(setOrgChannels).catch(() => {});
      if (SHOW_LEGACY_WORKFLOW_UI) loadNikaidoWorkflows();
      taskPipelineService.getTasksForAgent(id).then(tasks => {
        setPipelineTasks(tasks.sort((a, b) => a.sort_order - b.sort_order));
      }).catch(() => {});
      taskPipelineService.getReferenceTemplates().then(setReferenceTemplates).catch(() => {});
      notificationsService.getChannels().then(setOrgNotifChannels).catch(() => {});
      integrationsService.getIntegrations().then(setAvailableIntegrations).catch(() => {});
    }
  }, [id]);

  useEffect(() => {
    if (selectedAgent && !initialLoadDone.current) {
      initialLoadDone.current = true;
      setConfigForm({
        name: selectedAgent.name,
        description: selectedAgent.description || '',
        custom_instructions: selectedAgent.configuration.personality.custom_instructions,
        greeting_message: selectedAgent.configuration.personality.greeting_message,
        goodbye_message: selectedAgent.configuration.personality.goodbye_message,
        tone: selectedAgent.configuration.personality.tone,
        language: selectedAgent.configuration.personality.language,
        ai_provider: selectedAgent.configuration.ai_model.provider,
        ai_model: selectedAgent.configuration.ai_model.model,
        ai_temperature: selectedAgent.configuration.ai_model.temperature,
        ai_max_tokens: selectedAgent.configuration.ai_model.max_tokens,
        lead_qualification_enabled: selectedAgent.configuration.lead_qualification?.enabled || false,
        lead_qualification_criteria: selectedAgent.configuration.lead_qualification?.criteria || '',
        lead_scoring_enabled: selectedAgent.configuration.lead_scoring?.enabled || false,
        lead_scoring_high: selectedAgent.configuration.lead_scoring?.ranges?.high || '',
        lead_scoring_good: selectedAgent.configuration.lead_scoring?.ranges?.good || '',
        lead_scoring_medium: selectedAgent.configuration.lead_scoring?.ranges?.medium || '',
        lead_scoring_low: selectedAgent.configuration.lead_scoring?.ranges?.low || '',
        lead_scoring_minimal: selectedAgent.configuration.lead_scoring?.ranges?.minimal || '',
      });
      setIsDirty(false);
    }
  }, [selectedAgent]);

  useEffect(() => {
    // Plan 011 — legacy Nikaido workflow UI is hidden; skip its trigger-contract
    // loading so agent-detail load doesn't make unnecessary Nikaido API calls.
    if (!SHOW_LEGACY_WORKFLOW_UI) return;
    const wf = selectedAgent?.configuration?.nikaido_workflow;
    if (!wf?.workflow_id) {
      setTriggerContract(null);
      setCredentialBindings({});
      return;
    }
    loadTriggerContract(wf.workflow_id);
    // Pre-populate credential bindings from the saved agent config
    setCredentialBindings(wf.credential_bindings || {});
  }, [selectedAgent?.configuration?.nikaido_workflow?.workflow_id]);


  // ------------------------------------------------------------------
  // Knowledge loaders
  // ------------------------------------------------------------------
  const loadDocuments = async () => {
    if (!id) return;
    try {
      const docs = await knowledgeService.getAgentDocuments(id);
      setAgentDocuments(docs);
    } catch { /* ignore */ }
  };

  const loadSharedKnowledgeBases = async () => {
    if (!id) return;
    setIsLoadingKbs(true);
    try {
      const allKbs = await knowledgeService.getKnowledgeBases();
      setAllKnowledgeBases(allKbs);
    } catch { /* ignore */ }
    finally { setIsLoadingKbs(false); }
  };

  const loadNikaidoWorkflows = async () => {
    setIsLoadingWorkflows(true);
    try {
      const workflows = await nikaidoService.listWorkflows();
      setNikaidoWorkflows(workflows);
    } catch {
      setNikaidoWorkflows([]);
    } finally {
      setIsLoadingWorkflows(false);
    }
  };

  const loadTriggerContract = async (workflowId: string) => {
    setIsLoadingTriggerContract(true);
    try {
      const contract = await nikaidoService.getWorkflowTriggerContract(workflowId);
      setTriggerContract(contract);
    } catch {
      setTriggerContract(null);
    } finally {
      setIsLoadingTriggerContract(false);
    }
  };

  const handleSaveCredentialBindings = async () => {
    if (!id) return;

    const integrations = triggerContract?.integrations || [];
    const missing = integrations.filter(i => i.required && !credentialBindings[i.type]?.integration_id);
    if (missing.length > 0) {
      toast.error(`Select an integration for "${missing[0].displayName}" before saving`);
      return;
    }

    setIsSavingCredentialBindings(true);
    try {
      await updateWorkflowConfig(id, { credential_bindings: credentialBindings });
      toast.success('Credential bindings saved');
    } catch {
      toast.error('Failed to save credential bindings');
    } finally {
      setIsSavingCredentialBindings(false);
    }
  };

  const handleCreateWorkflow = async () => {
    if (!id || !selectedAgent) return;
    const name = newWorkflowName.trim() || `${selectedAgent.name} workflow`;
    setIsWorkflowBusy(true);
    try {
      const workflow = await nikaidoService.createWorkflow(name);
      await connectWorkflow(id, { workflow_id: workflow.id, name: workflow.name });
      await loadTriggerContract(workflow.id);
      setNewWorkflowName('');
      await loadNikaidoWorkflows();
      toast.success('Workflow created and connected');
      navigate(`/agents/${id}/workflow/${workflow.id}`);
    } catch {
      toast.error('Failed to create workflow');
    } finally {
      setIsWorkflowBusy(false);
    }
  };

  const handleConnectWorkflow = async (workflow: NikaidoWorkflow) => {
    if (!id) return;
    setIsWorkflowBusy(true);
    try {
      await connectWorkflow(id, { workflow_id: workflow.id, name: workflow.name });
      await loadTriggerContract(workflow.id);
      toast.success('Workflow connected');
    } catch {
      toast.error('Failed to connect workflow');
    } finally {
      setIsWorkflowBusy(false);
    }
  };

  const handleDisconnectWorkflow = async () => {
    if (!id) return;
    setIsWorkflowBusy(true);
    try {
      await disconnectWorkflow(id);
      setTriggerContract(null);
      setCredentialBindings({});
      toast.success('Workflow disconnected');
    } catch {
      toast.error('Failed to disconnect workflow');
    } finally {
      setIsWorkflowBusy(false);
    }
  };

  const openConnectedWorkflow = () => {
    const workflowId = selectedAgent?.configuration?.nikaido_workflow?.workflow_id;
    if (workflowId) openWorkflow(workflowId);
  };

  const openWorkflow = (workflowId: string) => {
    if (id && workflowId) {
      navigate(`/agents/${id}/workflow/${workflowId}`);
    }
  };

  // Single source of truth: derive linkedKbIds from agent's JSONB config
  const linkedKbIds = useMemo(
    () => new Set(selectedAgent?.configuration?.knowledge_base_ids || []),
    [selectedAgent?.configuration?.knowledge_base_ids]
  );

  // ------------------------------------------------------------------
  // Save configuration
  // ------------------------------------------------------------------
  // Plan 011 — persist the agent's single active-conversation tool integration.
  const handleSaveActiveConversationIntegration = async (integrationId: string) => {
    if (!id || !selectedAgent) return;
    setSavingActiveConv(true);
    try {
      await updateAgent(id, {
        configuration: {
          ...selectedAgent.configuration,
          active_conversation: {
            ...(selectedAgent.configuration.active_conversation || {}),
            integration_id: integrationId || undefined,
          },
        },
      });
      toast.success(integrationId ? 'Store integration saved' : 'Store integration cleared');
    } catch {
      toast.error('Failed to save integration');
    } finally {
      setSavingActiveConv(false);
    }
  };

  const handleSaveConfiguration = async () => {
    if (!id || !selectedAgent) return;
    setIsSaving(true);
    try {
      await updateAgent(id, {
        name: configForm.name,
        description: configForm.description,
        configuration: {
          ...selectedAgent.configuration,
          personality: {
            ...selectedAgent.configuration.personality,
            custom_instructions: configForm.custom_instructions,
            greeting_message: configForm.greeting_message,
            goodbye_message: configForm.goodbye_message,
            tone: configForm.tone,
            language: configForm.language,
          },
          ai_model: {
            provider: configForm.ai_provider as 'openai' | 'anthropic' | 'gemini' | 'mock',
            model: configForm.ai_model,
            temperature: configForm.ai_temperature,
            max_tokens: configForm.ai_max_tokens,
          },
          lead_qualification: {
            enabled: configForm.lead_qualification_enabled,
            criteria: configForm.lead_qualification_criteria,
          },
          lead_scoring: {
            enabled: configForm.lead_scoring_enabled,
            ranges: {
              high: configForm.lead_scoring_high,
              good: configForm.lead_scoring_good,
              medium: configForm.lead_scoring_medium,
              low: configForm.lead_scoring_low,
              minimal: configForm.lead_scoring_minimal,
            },
          },
        },
      });
      setIsDirty(false);
      toast.success('Changes saved');
    } catch {
      toast.error('Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const syncFormFromAgent = (agent: typeof selectedAgent) => {
    if (!agent) return;
    setConfigForm({
      name: agent.name,
      description: agent.description || '',
      custom_instructions: agent.configuration.personality.custom_instructions,
      greeting_message: agent.configuration.personality.greeting_message,
      goodbye_message: agent.configuration.personality.goodbye_message,
      tone: agent.configuration.personality.tone,
      language: agent.configuration.personality.language,
      ai_provider: agent.configuration.ai_model.provider,
      ai_model: agent.configuration.ai_model.model,
      ai_temperature: agent.configuration.ai_model.temperature,
      ai_max_tokens: agent.configuration.ai_model.max_tokens,
      lead_qualification_enabled: agent.configuration.lead_qualification?.enabled || false,
      lead_qualification_criteria: agent.configuration.lead_qualification?.criteria || '',
      lead_scoring_enabled: agent.configuration.lead_scoring?.enabled || false,
      lead_scoring_high: agent.configuration.lead_scoring?.ranges?.high || '',
      lead_scoring_good: agent.configuration.lead_scoring?.ranges?.good || '',
      lead_scoring_medium: agent.configuration.lead_scoring?.ranges?.medium || '',
      lead_scoring_low: agent.configuration.lead_scoring?.ranges?.low || '',
      lead_scoring_minimal: agent.configuration.lead_scoring?.ranges?.minimal || '',
    });
    setIsDirty(false);
  };

  const handleDiscard = () => {
    syncFormFromAgent(selectedAgent);
  };

  // ------------------------------------------------------------------
  // Knowledge base handlers
  // ------------------------------------------------------------------
  const handleToggleKb = async (kbId: string) => {
    if (!id || togglingKbId) return;
    setTogglingKbId(kbId);
    const isLinked = linkedKbIds.has(kbId);
    try {
      if (isLinked) {
        await knowledgeService.unlinkKnowledgeBaseFromAgent(id, kbId);
        toast.success('Knowledge base unlinked');
      } else {
        await knowledgeService.linkKnowledgeBaseToAgent(id, kbId);
        toast.success('Knowledge base linked');
      }
      // Re-fetch agent so JSONB-derived linkedKbIds updates
      await fetchAgent(id);
      await loadDocuments();
    } catch {
      toast.error('Failed to update knowledge base link');
    } finally {
      setTogglingKbId(null);
    }
  };

  const handleUploadKbFiles = async () => {
    if (!id || kbPendingFiles.length === 0) return;
    setIsUploadingKb(true);
    try {
      await knowledgeService.uploadFilesToAgent(id, kbPendingFiles);
      toast.success(`${kbPendingFiles.length} file(s) queued for processing`);
      setKbPendingFiles([]);
      await loadDocuments();
    } catch {
      toast.error('Failed to upload files');
    } finally {
      setIsUploadingKb(false);
    }
  };

  const handleDeleteKbDocument = async (docId: string) => {
    if (!id) return;
    if (!confirm('Are you sure you want to delete this document?')) return;
    try {
      await knowledgeService.deleteAgentDocument(id, docId);
      setAgentDocuments(prev => prev.filter(d => d.id !== docId));
      toast.success('Document deleted');
    } catch {
      toast.error('Failed to delete document');
    }
  };

  // ------------------------------------------------------------------
  // KB document browsing
  // ------------------------------------------------------------------
  const handleExpandKb = async (kbId: string) => {
    if (expandedKbId === kbId) {
      setExpandedKbId(null);
      return;
    }
    setExpandedKbId(kbId);
    if (!kbDocuments[kbId]) {
      setLoadingKbDocs(kbId);
      try {
        const docs = await knowledgeService.getDocuments(kbId);
        setKbDocuments(prev => ({ ...prev, [kbId]: docs }));
      } catch {
        // silent
      } finally {
        setLoadingKbDocs(null);
      }
    }
  };

  // ------------------------------------------------------------------
  // Agent-level handlers
  // ------------------------------------------------------------------
  const handleDelete = async () => {
    if (!id) return;
    if (confirm('Are you sure you want to delete this agent?')) {
      try {
        await deleteAgent(id);
        toast.success('Agent deleted successfully');
        navigate('/agents');
      } catch {
        toast.error('Failed to delete agent');
      }
    }
  };

  const handleStatusToggle = async () => {
    if (!id || !selectedAgent) return;
    const currentStatus = selectedAgent.status;
    const newStatus = currentStatus === 'active' ? 'paused' : 'active';
    try {
      await updateAgentStatus(id, newStatus);
      toast.success(`Agent ${newStatus === 'active' ? 'activated' : 'paused'}`);
    } catch (err: any) {
      // Parse backend validation error for actionable checklist
      const msg = err?.response?.data?.message || err?.message || '';
      if (msg.toLowerCase().includes('greeting')) {
        toast.error('Add a greeting message in Profile before activating');
      } else if (msg.toLowerCase().includes('knowledge')) {
        toast.error('Link at least one Knowledge Base before activating');
      } else if (msg.toLowerCase().includes('escalation')) {
        toast.error('Add at least one Escalation Rule before activating');
      } else {
        toast.error(msg || 'Failed to update agent status');
      }
    }
  };

  // ------------------------------------------------------------------
  // Escalation rule handlers
  // ------------------------------------------------------------------
  const openEscalationModal = (rule?: EscalationRule) => {
    if (rule) {
      setEditingEscalation(rule);
      setEscalationForm({
        name: rule.name,
        priority: rule.priority,
        guideline: rule.guideline || '',
        notification_message: rule.notification_channels[0]?.template || '',
        notification_channel_ids: rule.notification_channel_ids || [],
      });
    } else {
      setEditingEscalation(null);
      setEscalationForm({
        name: '',
        priority: 'normal',
        guideline: '',
        notification_message: '',
        notification_channel_ids: [],
      });
    }
    setActivePopup('escalation');
  };

  const handleSaveEscalation = async () => {
    if (!id || !selectedAgent) return;
    try {
      const newRule: EscalationRule = {
        id: editingEscalation?.id || `rule-${Date.now()}`,
        name: escalationForm.name,
        trigger_type: 'ai_evaluation', // LLM evaluates the guideline
        trigger_value: '',
        priority: escalationForm.priority,
        notification_channels: [],
        assignee_ids: [],
        guideline: escalationForm.guideline || undefined,
        notification_channel_ids: escalationForm.notification_channel_ids,
      };
      const updatedRules = editingEscalation
        ? selectedAgent.configuration.escalation_rules.map(r => r.id === editingEscalation.id ? newRule : r)
        : [...selectedAgent.configuration.escalation_rules, newRule];
      await updateAgent(id, {
        configuration: {
          ...selectedAgent.configuration,
          escalation_rules: updatedRules,
        },
      });
      toast.success(editingEscalation ? 'Escalation rule updated' : 'Escalation rule added');
      setActivePopup(null);
    } catch {
      toast.error('Failed to save escalation rule');
    }
  };

  const handleDeleteEscalation = async (ruleId: string) => {
    if (!id || !selectedAgent) return;
    if (!confirm('Are you sure you want to delete this escalation rule?')) return;
    try {
      const updatedRules = selectedAgent.configuration.escalation_rules.filter(r => r.id !== ruleId);
      await updateAgent(id, {
        configuration: {
          ...selectedAgent.configuration,
          escalation_rules: updatedRules,
        },
      });
      toast.success('Escalation rule deleted');
    } catch {
      toast.error('Failed to delete escalation rule');
    }
  };

  const toggleNotifChannelId = (channelId: string) => {
    setEscalationForm(prev => ({
      ...prev,
      notification_channel_ids: prev.notification_channel_ids.includes(channelId)
        ? prev.notification_channel_ids.filter(cid => cid !== channelId)
        : [...prev.notification_channel_ids, channelId],
    }));
  };

  // ------------------------------------------------------------------
  // Pipeline handlers
  // ------------------------------------------------------------------
  const resetPipelineForm = () => {
    setPipelineForm({
      name: '',
      task_type: 'data_collection',
      description: '',
      fields: [],
      document_guidance: '',
      reference_template_id: '',
      output_name_template: '',
      depends_on: '',
      workflow_name: '',
      destination_type: 'internal' as string,
      webhook_url: '',
      destination_webhook: false,
      destination_webhook_url: '',
      destination_integrations: [],
      notification_enabled: false,
      notification_channel_ids: '',
      notification_priority: 'normal' as 'low' | 'normal' | 'high' | 'urgent',
      business_hours_enabled: false,
      business_hours_timezone: 'UTC',
      business_hours_schedule: {},
      require_confirmation: true,
      action_type: 'submission' as 'order' | 'submission',
    });
    setPipelineWizardStep('choose');
    setEditingPipelineTask(null);
    setCustomFieldInput('');
  };

  const openEditPipelineStep = (task: AgentTask) => {
    setEditingPipelineTask(task);
    const ac = (task as any).action_config || {};
    setPipelineForm({
      name: task.name,
      task_type: task.task_type,
      description: task.description || '',
      fields: (task.collection_config?.fields || []).map(f => ({
        label: f.field_name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        key: f.field_name,
        type: f.field_type,
        required: f.required,
        prompt: f.prompt_question,
      })),
      document_guidance: task.document_config?.guidance || '',
      reference_template_id: task.document_config?.reference_template_id || '',
      output_name_template: task.document_config?.output_name_template || '',
      depends_on: task.depends_on[0] || '',
      workflow_name: ac.workflow_name || '',
      destination_type: ac.destination?.type || 'internal',
      webhook_url: ac.destination?.webhook_url || '',
      destination_webhook: ac.destination?.type === 'webhook',
      destination_webhook_url: ac.destination?.webhook_url || '',
      destination_integrations: ac.destination?.integration_id ? [ac.destination.integration_id] : [],
      notification_enabled: !!ac.notification?.channel_ids?.length,
      notification_channel_ids: (ac.notification?.channel_ids || []).join(', '),
      notification_priority: ac.notification?.priority || 'normal',
      business_hours_enabled: !!ac.constraints?.business_hours?.enabled,
      business_hours_timezone: ac.constraints?.business_hours?.timezone || 'UTC',
      business_hours_schedule: ac.constraints?.business_hours?.schedule || {},
      require_confirmation: ac.require_confirmation ?? true,
      action_type: (ac.action_type || 'submission') as 'order' | 'submission',
    });
    setPipelineWizardStep('configure');
    setActivePopup('pipeline-add');
  };

  const addSuggestedField = (field: typeof SUGGESTED_FIELDS[0]) => {
    if (pipelineForm.fields.some(f => f.key === field.key)) return;
    setPipelineForm(prev => ({
      ...prev,
      fields: [...prev.fields, { ...field, required: true }],
    }));
  };

  const addCustomField = () => {
    const label = customFieldInput.trim();
    if (!label) return;
    const key = generateKey(label);
    if (pipelineForm.fields.some(f => f.key === key)) {
      toast.error('Field already exists');
      return;
    }
    setPipelineForm(prev => ({
      ...prev,
      fields: [...prev.fields, {
        label,
        key,
        type: detectFieldType(label),
        required: true,
        prompt: generatePrompt(label),
      }],
    }));
    setCustomFieldInput('');
  };

  const handleSavePipelineStep = async () => {
    if (!id || !pipelineForm.name.trim()) {
      toast.error('Step name is required');
      return;
    }
    try {
      const taskData: Partial<AgentTask> = {
        name: pipelineForm.name,
        task_type: pipelineForm.task_type,
        description: pipelineForm.description,
        is_active: true,
        depends_on: pipelineForm.depends_on ? [pipelineForm.depends_on] : [],
        collection_config: {
          fields: pipelineForm.fields.map(f => ({
            field_name: f.key,
            field_type: f.type as 'text' | 'email' | 'phone' | 'number' | 'date' | 'custom',
            required: f.required,
            prompt_question: f.prompt,
          })),
        },
        document_config: {
          guidance: pipelineForm.document_guidance,
          ...(pipelineForm.reference_template_id ? { reference_template_id: pipelineForm.reference_template_id } : {}),
          ...(pipelineForm.output_name_template ? { output_name_template: pipelineForm.output_name_template } : {}),
        },
        goal_config: pipelineForm.task_type === 'goal'
          ? { instructions: pipelineForm.description, is_primary: true }
          : undefined,
        action_config: pipelineForm.task_type === 'action' ? {
          action_type: pipelineForm.action_type,
          workflow_name: pipelineForm.name,
          destination: {
            type: (pipelineForm.destination_integrations || []).length > 0
              ? (availableIntegrations.find(i => i.id === pipelineForm.destination_integrations[0])?.platform_type || 'internal')
              : 'internal',
            ...((pipelineForm.destination_integrations || []).length > 0 ? { integration_id: pipelineForm.destination_integrations[0] } : {}),
          },
          ...(pipelineForm.notification_enabled ? {
            notification: {
              channel_ids: pipelineForm.notification_channel_ids.split(',').map((s: string) => s.trim()).filter(Boolean),
              priority: pipelineForm.notification_priority,
            },
          } : {}),
          ...(pipelineForm.business_hours_enabled ? {
            constraints: {
              business_hours: {
                enabled: true,
                timezone: pipelineForm.business_hours_timezone,
                schedule: pipelineForm.business_hours_schedule,
                off_hours_behavior: 'block' as const,
              },
            },
          } : {}),
          require_confirmation: pipelineForm.require_confirmation,
        } : undefined,
      };

      if (editingPipelineTask) {
        const updated = await taskPipelineService.updateTask(id, editingPipelineTask.id, taskData);
        setPipelineTasks(prev => prev.map(t => t.id === editingPipelineTask.id ? updated : t));
        toast.success('Step updated');
      } else {
        const created = await taskPipelineService.createTask(id, taskData);
        setPipelineTasks(prev => [...prev, created]);
        toast.success('Step added');
      }
      setActivePopup(null);
      resetPipelineForm();
    } catch {
      toast.error('Failed to save step');
    }
  };

  // ------------------------------------------------------------------
  // Derived data
  // ------------------------------------------------------------------
  const connectedChannels = orgChannels.filter(ch => ch.agents?.some(a => a.agent_id === id));

  // ------------------------------------------------------------------
  // Loading state
  // ------------------------------------------------------------------
  if (!selectedAgent) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-6rem)]">
        <Loader2 className="w-8 h-8 text-violet-600 animate-spin" />
      </div>
    );
  }

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  return (
    <div className="flex flex-col lg:flex-row h-[calc(100vh-6rem)] gap-0">
      {/* ======================= LEFT: Config panel ======================= */}
      <div className="flex-1 flex flex-col min-w-0 w-full lg:w-auto overflow-hidden">

        {/* --- Sticky header --- */}
        <div className="sticky top-0 z-10 bg-white border-b-2 border-[#1a1a2e] px-6 py-3">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/agents')} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
              <ArrowLeft className="h-4 w-4 text-gray-500" />
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2.5">
                <h1 className="text-lg font-semibold text-gray-900 truncate">{selectedAgent.name}</h1>
                <span className="text-xs text-gray-400 capitalize">{selectedAgent.type.replace('_', ' ')}</span>
                <StatusBadge status={selectedAgent.status} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              {selectedAgent.status !== 'archived' && (
                <button
                  onClick={handleStatusToggle}
                  className={cn(
                    'inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                    selectedAgent.status === 'active'
                      ? 'bg-orange-50 text-orange-700 hover:bg-orange-100'
                      : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
                  )}
                >
                  {selectedAgent.status === 'active' ? (
                    <><Pause className="h-4 w-4" /> Pause</>
                  ) : (
                    <><Play className="h-4 w-4" /> Activate</>
                  )}
                </button>
              )}
              <button
                onClick={handleDelete}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* --- Scrollable sections --- */}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">

          {/* Activation checklist for draft/testing agents */}
          {(selectedAgent.status === 'draft' || selectedAgent.status === 'testing') && (() => {
            const hasGreeting = !!selectedAgent.configuration?.personality?.greeting_message;
            const hasInstructions = !!selectedAgent.configuration?.personality?.custom_instructions?.trim();
            const hasKB = (selectedAgent.configuration?.knowledge_base_ids?.length || 0) > 0;
            const hasEscalation = (selectedAgent.configuration?.escalation_rules?.length || 0) > 0;
            // Note: AI key validation happens server-side on activation attempt
            const allReady = hasGreeting && hasInstructions && hasKB && hasEscalation;
            return (
              <div className={cn('rounded-xl border p-4 text-sm', allReady ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200')}>
                <p className={cn('font-semibold mb-2', allReady ? 'text-emerald-800' : 'text-amber-800')}>
                  {allReady ? 'Ready to activate!' : 'Complete these steps to activate:'}
                </p>
                <ul className="space-y-1">
                  <li className={cn('flex items-center gap-2', hasGreeting ? 'text-emerald-700' : 'text-amber-700')}>
                    {hasGreeting ? <CheckCircle className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                    Greeting message {hasGreeting ? '' : '— add in Personality section'}
                  </li>
                  <li className={cn('flex items-center gap-2', hasInstructions ? 'text-emerald-700' : 'text-amber-700')}>
                    {hasInstructions ? <CheckCircle className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                    Custom instructions {hasInstructions ? '' : '— add in Personality section'}
                  </li>
                  <li className={cn('flex items-center gap-2', hasKB ? 'text-emerald-700' : 'text-amber-700')}>
                    {hasKB ? <CheckCircle className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                    Knowledge base {hasKB ? '' : '— link in Knowledge section'}
                  </li>
                  <li className={cn('flex items-center gap-2', hasEscalation ? 'text-emerald-700' : 'text-amber-700')}>
                    {hasEscalation ? <CheckCircle className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                    Escalation rule {hasEscalation ? '' : '— add in Escalation section'}
                  </li>
                </ul>
              </div>
            );
          })()}

          {/* ======== Compact Summary Cards ======== */}
          <div className="space-y-3">

            {/* ── Profile ── */}
            <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[2px_2px_0px_#1a1a2e] p-4 flex items-start justify-between gap-3 hover:shadow-[3px_3px_0px_#1a1a2e] transition-all">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-violet-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900">Profile</p>
                  <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">
                    {configForm.name}{configForm.description ? ` — ${configForm.description.substring(0, 80)}` : ''}{' '}<span className="text-gray-400 capitalize">{selectedAgent.type.replace('_', ' ')}</span>
                  </p>
                </div>
              </div>
              <button onClick={() => setActivePopup('profile')} className="text-xs font-medium text-violet-600 hover:text-violet-700 px-2 py-1 rounded-lg hover:bg-violet-50 transition-colors flex-shrink-0">
                Edit
              </button>
            </div>

            {/* ── Instructions ── */}
            <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[2px_2px_0px_#1a1a2e] p-4 flex items-start justify-between gap-3 hover:shadow-[3px_3px_0px_#1a1a2e] transition-all">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-4 h-4 text-violet-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900">Instructions</p>
                  <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                    {selectedAgent.configuration?.personality?.custom_instructions?.substring(0, 120) || 'No instructions set — tell your agent how to behave'}
                  </p>
                </div>
              </div>
              <button onClick={() => setActivePopup('instructions')} className="text-xs font-medium text-violet-600 hover:text-violet-700 px-2 py-1 rounded-lg hover:bg-violet-50 transition-colors flex-shrink-0">
                Edit
              </button>
            </div>

            {/* ── Knowledge ── */}
            {(() => {
              const linkedKbs = allKnowledgeBases.filter(kb => linkedKbIds.has(kb.id));
              const docCount = agentDocuments.length;
              const kbSummary = linkedKbs.length > 0
                ? `${linkedKbs.length} knowledge base${linkedKbs.length !== 1 ? 's' : ''}${docCount > 0 ? ` · ${docCount} doc${docCount !== 1 ? 's' : ''}` : ''} · ${linkedKbs.map(kb => kb.name).join(' & ')}`
                : 'No knowledge added — upload documents for your agent';
              return (
                <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[2px_2px_0px_#1a1a2e] overflow-hidden hover:shadow-[3px_3px_0px_#1a1a2e] transition-all">
                  <div className="p-4 flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                        <Database className="w-4 h-4 text-emerald-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900">Knowledge</p>
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{kbSummary}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {(linkedKbs.length > 0 || docCount > 0) && (
                        <button onClick={() => setExpandedSection(expandedSection === 'knowledge' ? null : 'knowledge')} className="text-xs font-medium text-emerald-600 hover:text-emerald-700 px-2 py-1 rounded-lg hover:bg-emerald-50 transition-colors">
                          {expandedSection === 'knowledge' ? 'Collapse' : 'View'}
                        </button>
                      )}
                      <button onClick={() => setActivePopup('knowledge')} className="text-xs font-medium text-emerald-600 hover:text-emerald-700 px-2 py-1 rounded-lg hover:bg-emerald-50 transition-colors">
                        Edit
                      </button>
                    </div>
                  </div>
                  {/* Expanded knowledge list */}
                  {expandedSection === 'knowledge' && (linkedKbs.length > 0 || docCount > 0) && (
                    <div className="border-t border-gray-100 px-4 py-3 space-y-1">
                      {linkedKbs.map(kb => (
                        <div key={kb.id} className="flex items-center gap-3 py-1.5 px-2 rounded-lg hover:bg-gray-50 group transition-colors">
                          <Database className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                          <span className="text-xs text-gray-700 flex-1 truncate">{kb.name}</span>
                          <span className="text-[10px] text-gray-300">{kb.document_count ?? 0} docs</span>
                          <button onClick={() => handleToggleKb(kb.id)} className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-50 transition-all">
                            <X className="w-3 h-3 text-red-400" />
                          </button>
                        </div>
                      ))}
                      {agentDocuments.map(doc => (
                        <div key={doc.id} className="flex items-center gap-3 py-1.5 px-2 rounded-lg hover:bg-gray-50 group transition-colors">
                          <FileText className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                          <span className="text-xs text-gray-700 flex-1 truncate">{doc.name}</span>
                          {doc.knowledge_base_name && (
                            <span className="text-[10px] text-gray-300 flex-shrink-0">{doc.knowledge_base_name}</span>
                          )}
                          <span className="text-[10px] text-gray-300">{doc.status}</span>
                          <button
                            onClick={() => handleDeleteKbDocument(doc.id)}
                            className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-50 transition-all"
                          >
                            <X className="w-3 h-3 text-red-400" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* ── Nikaido Workflow (Plan 011 — hidden behind SHOW_LEGACY_WORKFLOW_UI) ── */}
            {SHOW_LEGACY_WORKFLOW_UI && (() => {
              const connectedWorkflow = selectedAgent.configuration?.nikaido_workflow;
              const workflowSummary = connectedWorkflow
                ? `${connectedWorkflow.name || connectedWorkflow.workflow_id} · ${connectedWorkflow.enabled ? 'Connected' : 'Disabled'}`
                : 'No workflow connected — create or select a Nikaido workflow';

              return (
                <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[2px_2px_0px_#1a1a2e] overflow-hidden hover:shadow-[3px_3px_0px_#1a1a2e] transition-all">
                  <div className="p-4 flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="w-8 h-8 rounded-lg bg-cyan-50 flex items-center justify-center flex-shrink-0">
                        <Workflow className="w-4 h-4 text-cyan-700" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-gray-900">Workflow automation</p>
                          {connectedWorkflow && (
                            <span className="text-[10px] font-medium text-cyan-700 bg-cyan-50 px-1.5 py-0.5 rounded">1 connected</span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{workflowSummary}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {connectedWorkflow && (
                        <button
                          onClick={openConnectedWorkflow}
                          className="text-xs font-medium text-cyan-700 hover:text-cyan-800 px-2 py-1 rounded-lg hover:bg-cyan-50 transition-colors inline-flex items-center gap-1"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                          Open
                        </button>
                      )}
                      <button
                        onClick={() => setExpandedSection(expandedSection === 'nikaido-workflow' ? null : 'nikaido-workflow')}
                        className="text-xs font-medium text-cyan-700 hover:text-cyan-800 px-2 py-1 rounded-lg hover:bg-cyan-50 transition-colors"
                      >
                        {expandedSection === 'nikaido-workflow' ? 'Collapse' : 'Edit'}
                      </button>
                    </div>
                  </div>

                  {expandedSection === 'nikaido-workflow' && (
                    <div className="border-t border-gray-100 px-4 py-4 space-y-4">
                      <div className="flex flex-col sm:flex-row gap-2">
                        <input
                          value={newWorkflowName}
                          onChange={(e) => setNewWorkflowName(e.target.value)}
                          placeholder={`${selectedAgent.name} workflow`}
                          className="flex-1 h-9 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-100 focus:border-cyan-500"
                        />
                        <button
                          onClick={handleCreateWorkflow}
                          disabled={isWorkflowBusy}
                          className="inline-flex items-center justify-center gap-2 px-3 h-9 rounded-lg bg-cyan-700 text-white text-sm font-medium hover:bg-cyan-800 disabled:opacity-60"
                        >
                          {isWorkflowBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                          Create workflow
                        </button>
                      </div>

                      {connectedWorkflow && (
                        <div className="flex items-center gap-3 p-3 rounded-lg border border-cyan-100 bg-cyan-50">
                          <Workflow className="w-4 h-4 text-cyan-700 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{connectedWorkflow.name || connectedWorkflow.workflow_id}</p>
                            <p className="text-xs text-cyan-700 truncate">Connected to this agent</p>
                          </div>
                          <button
                            onClick={openConnectedWorkflow}
                            className="p-2 rounded-lg text-cyan-700 hover:bg-white"
                            title="Open workflow"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </button>
                          <button
                            onClick={handleDisconnectWorkflow}
                            disabled={isWorkflowBusy}
                            className="p-2 rounded-lg text-red-600 hover:bg-white disabled:opacity-60"
                            title="Disconnect workflow"
                          >
                            <Unlink className="w-4 h-4" />
                          </button>
                        </div>
                      )}

                      {connectedWorkflow && (
                        <div className="rounded-lg border border-gray-100 bg-gray-50 p-3 space-y-3">
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Credential bindings</p>
                              <p className="text-xs text-gray-400 mt-0.5">Bind your integrations to the systems this workflow needs.</p>
                            </div>
                            <button
                              onClick={() => loadTriggerContract(connectedWorkflow.workflow_id)}
                              disabled={isLoadingTriggerContract}
                              className="p-1.5 rounded-lg text-gray-500 hover:bg-white disabled:opacity-60"
                              title="Refresh trigger contract"
                            >
                              <RefreshCw className={cn('w-4 h-4', isLoadingTriggerContract && 'animate-spin')} />
                            </button>
                          </div>

                          {isLoadingTriggerContract ? (
                            <div className="py-4 flex items-center justify-center text-sm text-gray-400">
                              <Loader2 className="w-4 h-4 animate-spin mr-2" />
                              Loading integration requirements...
                            </div>
                          ) : !triggerContract ? (
                            <p className="text-sm text-gray-400 py-2">Trigger contract is unavailable for this workflow.</p>
                          ) : triggerContract.integrations.length === 0 ? (
                            <p className="text-sm text-gray-400 py-2">This workflow has no integration requirements.</p>
                          ) : (
                            <div className="space-y-3">
                              {triggerContract.integrations.map((intReq) => {
                                const matchingIntegrations = availableIntegrations.filter(
                                  (i) => i.platform_type === intReq.type
                                );
                                const selectedId = credentialBindings[intReq.type]?.integration_id || '';
                                return (
                                  <div key={intReq.type} className="grid grid-cols-1 lg:grid-cols-[180px_1fr] gap-2 lg:items-center">
                                    <div>
                                      <p className="text-sm font-medium text-gray-800">
                                        {intReq.displayName}
                                        {intReq.required ? (
                                          <span className="text-red-500 ml-1">*</span>
                                        ) : (
                                          <span className="text-gray-400 ml-1 text-xs">(optional)</span>
                                        )}
                                      </p>
                                      <p className="text-xs text-gray-400">{intReq.type}</p>
                                    </div>
                                    <select
                                      value={selectedId}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        setCredentialBindings((prev) => {
                                          if (!value) {
                                            const next = { ...prev };
                                            delete next[intReq.type];
                                            return next;
                                          }
                                          return {
                                            ...prev,
                                            [intReq.type]: { integration_id: value },
                                          };
                                        });
                                      }}
                                      className="h-9 px-3 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-cyan-100 focus:border-cyan-500"
                                    >
                                      <option value="">Select integration</option>
                                      {matchingIntegrations.map((integration) => (
                                        <option key={integration.id} value={integration.id}>
                                          {integration.name} ({integration.status})
                                        </option>
                                      ))}
                                    </select>
                                    {matchingIntegrations.length === 0 && (
                                      <p className="text-xs text-amber-600 lg:col-start-2">
                                        No {intReq.type.replace(/_/g, ' ')} integrations found. Add one from Integrations first.
                                      </p>
                                    )}
                                  </div>
                                );
                              })}

                              {availableIntegrations.length === 0 && (
                                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                                  No integrations connected yet. Add your integrations from the Integrations page, then come back here.
                                </p>
                              )}

                              <div className="flex justify-end">
                                <button
                                  onClick={handleSaveCredentialBindings}
                                  disabled={isSavingCredentialBindings}
                                  className="inline-flex items-center justify-center gap-2 px-3 h-9 rounded-lg bg-cyan-700 text-white text-sm font-medium hover:bg-cyan-800 disabled:opacity-60"
                                >
                                  {isSavingCredentialBindings && <Loader2 className="w-4 h-4 animate-spin" />}
                                  Save bindings
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Existing workflows</p>
                          <button
                            onClick={loadNikaidoWorkflows}
                            disabled={isLoadingWorkflows}
                            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-50 disabled:opacity-60"
                            title="Refresh workflows"
                          >
                            <RefreshCw className={cn('w-4 h-4', isLoadingWorkflows && 'animate-spin')} />
                          </button>
                        </div>

                        {isLoadingWorkflows ? (
                          <div className="py-4 flex items-center justify-center text-sm text-gray-400">
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            Loading workflows...
                          </div>
                        ) : nikaidoWorkflows.length === 0 ? (
                          <p className="text-sm text-gray-400 py-3">No Nikaido workflows found for this organization.</p>
                        ) : (
                          <div className="max-h-56 overflow-y-auto divide-y divide-gray-100 border border-gray-100 rounded-lg">
                            {nikaidoWorkflows.map((workflow) => {
                              const isConnected = connectedWorkflow?.workflow_id === workflow.id;
                              return (
                                <div key={workflow.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50">
                                  <Workflow className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                  <button
                                    onClick={() => openWorkflow(workflow.id)}
                                    className="flex-1 min-w-0 text-left"
                                  >
                                    <p className="text-sm font-medium text-gray-800 truncate">{workflow.name}</p>
                                    <p className="text-xs text-gray-400">{workflow.active ? 'Active' : workflow.status || 'Draft'}</p>
                                  </button>
                                  <button
                                    onClick={() => openWorkflow(workflow.id)}
                                    className="p-1.5 rounded-lg text-gray-500 hover:bg-white"
                                    title="Open workflow"
                                  >
                                    <ExternalLink className="w-4 h-4" />
                                  </button>
                                  {isConnected ? (
                                    <span className="text-xs font-medium text-cyan-700 bg-cyan-50 px-2 py-1 rounded">Selected</span>
                                  ) : (
                                    <button
                                      onClick={() => handleConnectWorkflow(workflow)}
                                      disabled={isWorkflowBusy}
                                      className="text-xs font-medium text-cyan-700 hover:text-cyan-800 px-2 py-1 rounded-lg hover:bg-cyan-50 disabled:opacity-60"
                                    >
                                      Select
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* ── Active conversation (Plan 011) ── */}
            <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[2px_2px_0px_#1a1a2e] overflow-hidden hover:shadow-[3px_3px_0px_#1a1a2e] transition-all">
              <div className="p-4 flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-cyan-50 flex items-center justify-center flex-shrink-0">
                  <Workflow className="w-4 h-4 text-cyan-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900">Active conversation</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    This agent replies through the shared AI active-conversation workflow. Optionally bind
                    one store/tool integration it can use to answer product questions and take actions.
                  </p>
                  <div className="mt-3">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Tool integration (optional)</label>
                    {(() => {
                      const supported = availableIntegrations.filter(
                        (i) => i.platform_type === 'woocommerce' || i.platform_type === 'shopify'
                      );
                      const selectedId = selectedAgent.configuration.active_conversation?.integration_id || '';
                      return (
                        <>
                          <select
                            value={selectedId}
                            disabled={savingActiveConv}
                            onChange={(e) => handleSaveActiveConversationIntegration(e.target.value)}
                            className="w-full h-9 px-3 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-cyan-100 focus:border-cyan-500 disabled:opacity-60"
                          >
                            <option value="">No integration</option>
                            {supported.map((i) => (
                              <option key={i.id} value={i.id}>
                                {i.name} ({i.platform_type} · {i.status})
                              </option>
                            ))}
                          </select>
                          {supported.length === 0 && (
                            <p className="text-xs text-amber-600 mt-1">
                              No WooCommerce/Shopify integrations found. Add one from Integrations first.
                            </p>
                          )}
                          {savingActiveConv && (
                            <p className="text-xs text-gray-400 mt-1 inline-flex items-center gap-1">
                              <Loader2 className="w-3 h-3 animate-spin" /> Saving…
                            </p>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>

            {/* ── Pipeline (What it collects / does) ── */}
            <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[2px_2px_0px_#1a1a2e] overflow-hidden hover:shadow-[3px_3px_0px_#1a1a2e] transition-all">
              <div className="p-4 flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <ListChecks className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-gray-900">What it collects & does</p>
                      {pipelineTasks.length > 0 && (
                        <span className="text-[10px] font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">{pipelineTasks.length} step{pipelineTasks.length !== 1 ? 's' : ''}</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">
                      {pipelineTasks.length > 0
                        ? pipelineTasks.map(t => t.name).join(' \u2192 ')
                        : 'No workflow configured'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => setExpandedSection(expandedSection === 'pipeline' ? null : 'pipeline')} className="text-xs font-medium text-blue-600 hover:text-blue-700 px-2 py-1 rounded-lg hover:bg-blue-50 transition-colors">
                    {expandedSection === 'pipeline' ? 'Collapse' : 'Edit'}
                  </button>
                  <button onClick={() => { resetPipelineForm(); setActivePopup('pipeline-add'); }} className="text-xs font-medium text-blue-600 hover:text-blue-700 p-1 rounded-lg hover:bg-blue-50 transition-colors">
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              {/* Expanded pipeline view */}
              {expandedSection === 'pipeline' && (
                <div className="border-t border-gray-100 px-4 py-3">
                  {/* Visual flow */}
                  {pipelineTasks.length > 0 && (
                    <div className="flex items-center gap-1 flex-wrap text-xs text-gray-500 mb-3">
                      {pipelineTasks.map((t, i) => (
                        <React.Fragment key={t.id}>
                          <span className="px-2 py-0.5 bg-blue-50 rounded text-blue-700 font-medium">{t.name}</span>
                          {i < pipelineTasks.length - 1 && <span className="text-gray-300">{'\u2192'}</span>}
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                  {/* Step list */}
                  {pipelineTasks.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-sm text-gray-400">No pipeline steps yet</p>
                      <button onClick={() => { resetPipelineForm(); setActivePopup('pipeline-add'); }} className="text-xs text-violet-600 font-medium mt-2">Add a step</button>
                    </div>
                  ) : (
                    <div className="space-y-0">
                      {pipelineTasks.map((task, idx) => (
                        <div key={task.id}>
                          <div className="flex items-start gap-3 py-2.5 px-2 rounded-lg hover:bg-gray-50 group transition-colors">
                            <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-[10px] font-bold flex-shrink-0 mt-0.5">
                              {idx + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900">{task.name}</p>
                              <p className="text-xs text-gray-400 mt-0.5 break-words">
                                {task.task_type === 'data_collection'
                                  ? `Collects: ${(task.collection_config?.fields || []).map(f => (f.field_name || '').replace(/_/g, ' ')).filter(Boolean).join(', ') || 'No fields'}`
                                  : task.task_type === 'document_creation'
                                    ? 'Generates a document'
                                    : task.task_type === 'goal'
                                      ? 'Goal'
                                      : task.task_type === 'action'
                                        ? 'Action'
                                        : task.task_type}
                                {task.depends_on.length > 0 && ` \u00B7 After: ${pipelineTasks.find(t => t.id === task.depends_on[0])?.name || 'previous step'}`}
                              </p>
                            </div>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
                              {idx > 0 && (
                                <button
                                  onClick={async () => {
                                    const newOrder = [...pipelineTasks];
                                    [newOrder[idx - 1], newOrder[idx]] = [newOrder[idx], newOrder[idx - 1]];
                                    setPipelineTasks(newOrder);
                                    await taskPipelineService.reorderTasks(id!, newOrder.map(t => t.id));
                                  }}
                                  className="p-1 rounded hover:bg-gray-200"
                                >
                                  <ChevronUp className="w-3 h-3 text-gray-400" />
                                </button>
                              )}
                              {idx < pipelineTasks.length - 1 && (
                                <button
                                  onClick={async () => {
                                    const newOrder = [...pipelineTasks];
                                    [newOrder[idx], newOrder[idx + 1]] = [newOrder[idx + 1], newOrder[idx]];
                                    setPipelineTasks(newOrder);
                                    await taskPipelineService.reorderTasks(id!, newOrder.map(t => t.id));
                                  }}
                                  className="p-1 rounded hover:bg-gray-200"
                                >
                                  <ChevronDown className="w-3 h-3 text-gray-400" />
                                </button>
                              )}
                              <button onClick={() => openEditPipelineStep(task)} className="p-1 rounded hover:bg-gray-200">
                                <Edit className="w-3 h-3 text-gray-400" />
                              </button>
                              <button
                                onClick={async () => {
                                  if (!confirm('Delete this step?')) return;
                                  await taskPipelineService.deleteTask(id!, task.id);
                                  setPipelineTasks(prev => prev.filter(t => t.id !== task.id));
                                  toast.success('Step deleted');
                                }}
                                className="p-1 rounded hover:bg-red-50"
                              >
                                <Trash2 className="w-3 h-3 text-red-400" />
                              </button>
                            </div>
                          </div>
                          {idx < pipelineTasks.length - 1 && (
                            <div className="flex justify-center py-0.5"><div className="w-px h-3 bg-gray-200" /></div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ── Escalation Rules (Human takes over when) ── */}
            {(() => {
              const rules = selectedAgent.configuration.escalation_rules;
              const ruleNames = rules.map((r: EscalationRule) => r.name);
              const shown = ruleNames.slice(0, 2);
              const rest = ruleNames.length - shown.length;
              const summary = rules.length > 0
                ? shown.join(' · ') + (rest > 0 ? ` · +${rest} more` : '')
                : 'No escalation rules — your agent handles everything';
              return (
                <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[2px_2px_0px_#1a1a2e] overflow-hidden hover:shadow-[3px_3px_0px_#1a1a2e] transition-all">
                  <div className="p-4 flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                        <AlertTriangle className="w-4 h-4 text-red-500" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-gray-900">Human takes over when</p>
                          {rules.length > 0 && (
                            <span className="text-[10px] font-medium text-red-600 bg-red-50 px-1.5 py-0.5 rounded">{rules.length}</span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{summary}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => setExpandedSection(expandedSection === 'escalation' ? null : 'escalation')} className="text-xs font-medium text-red-600 hover:text-red-700 px-2 py-1 rounded-lg hover:bg-red-50 transition-colors">
                        {expandedSection === 'escalation' ? 'Collapse' : 'View'}
                      </button>
                      <button onClick={() => openEscalationModal()} className="text-xs font-medium text-red-600 hover:text-red-700 p-1 rounded-lg hover:bg-red-50 transition-colors">
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  {/* Expanded escalation list */}
                  {expandedSection === 'escalation' && (
                    <div className="border-t border-gray-100 px-4 py-3">
                      {rules.length === 0 ? (
                        <div className="text-center py-4">
                          <p className="text-sm text-gray-400">No escalation rules configured</p>
                          <button onClick={() => openEscalationModal()} className="text-xs text-violet-600 font-medium mt-2">Add a rule</button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {rules.map((rule: EscalationRule) => (
                            <div key={rule.id} className="flex items-start gap-3 py-2 px-2 rounded-lg hover:bg-gray-50 group transition-colors">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-gray-900">{rule.name}</span>
                                  <Badge variant={rule.priority === 'urgent' ? 'destructive' : rule.priority === 'high' ? 'warning' : 'default'}>
                                    {rule.priority}
                                  </Badge>
                                </div>
                                {rule.guideline && (
                                  <p className="mt-0.5 text-xs text-gray-400 line-clamp-1">{rule.guideline}</p>
                                )}
                              </div>
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
                                <button onClick={() => openEscalationModal(rule)} className="p-1 rounded hover:bg-gray-200">
                                  <Edit className="w-3 h-3 text-gray-400" />
                                </button>
                                <button onClick={() => handleDeleteEscalation(rule.id)} className="p-1 rounded hover:bg-red-50">
                                  <Trash2 className="w-3 h-3 text-red-400" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* ── Channels (Available on) ── */}
            {(() => {
              const chSummary = connectedChannels.length > 0
                ? connectedChannels.map(ch => `${ch.name} (${ch.channel_type})`).join(' · ')
                : 'No channels connected';
              return (
                <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[2px_2px_0px_#1a1a2e] p-4 flex items-start justify-between gap-3 hover:shadow-[3px_3px_0px_#1a1a2e] transition-all">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                      <Radio className="w-4 h-4 text-amber-600" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-gray-900">Available on</p>
                        {connectedChannels.length > 0 && (
                          <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">{connectedChannels.length}</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{chSummary}</p>
                    </div>
                  </div>
                  <button onClick={() => setActivePopup('channels')} className="text-xs font-medium text-amber-600 hover:text-amber-700 px-2 py-1 rounded-lg hover:bg-amber-50 transition-colors flex-shrink-0">
                    Edit
                  </button>
                </div>
              );
            })()}

            {/* ── Lead Scoring ── */}
            <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[2px_2px_0px_#1a1a2e] overflow-hidden hover:shadow-[3px_3px_0px_#1a1a2e] transition-all">
              <div className="p-4 flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="w-8 h-8 rounded-lg bg-rose-50 flex items-center justify-center flex-shrink-0">
                    <Target className="w-4 h-4 text-rose-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900">Lead Scoring</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {configForm.lead_scoring_enabled
                        ? `Enabled · Score 1-10 with ${[configForm.lead_scoring_high, configForm.lead_scoring_good, configForm.lead_scoring_medium, configForm.lead_scoring_low, configForm.lead_scoring_minimal].filter(Boolean).length} criteria`
                        : 'Disabled'}
                    </p>
                  </div>
                </div>
                <button onClick={() => setExpandedSection(expandedSection === 'lead-scoring' ? null : 'lead-scoring')} className="text-xs font-medium text-rose-600 hover:text-rose-700 px-2 py-1 rounded-lg hover:bg-rose-50 transition-colors flex-shrink-0">
                  {expandedSection === 'lead-scoring' ? 'Collapse' : 'Edit'}
                </button>
              </div>
              {/* Expanded lead scoring */}
              {expandedSection === 'lead-scoring' && (
                <div className="border-t border-gray-100 px-4 py-3">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm text-gray-700">Score customers automatically (1-10)</span>
                    <button
                      onClick={() => { setConfigForm(prev => ({ ...prev, lead_scoring_enabled: !prev.lead_scoring_enabled })); setIsDirty(true); }}
                      className={cn('relative inline-flex h-6 w-11 rounded-full transition-colors', configForm.lead_scoring_enabled ? 'bg-rose-500' : 'bg-gray-200')}
                    >
                      <span className={cn('inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform mt-0.5', configForm.lead_scoring_enabled ? 'translate-x-5 ml-0.5' : 'translate-x-0.5')} />
                    </button>
                  </div>
                  {configForm.lead_scoring_enabled && (
                    <div className="space-y-3">
                      <p className="text-xs text-gray-500 mb-2">Describe what each score range means for your business:</p>
                      {[
                        { key: 'high', label: '9-10', color: 'bg-emerald-500', placeholder: 'e.g., Orders catering, corporate events, bulk orders over $500' },
                        { key: 'good', label: '7-8', color: 'bg-blue-500', placeholder: 'e.g., Regular repeat customer, orders weekly' },
                        { key: 'medium', label: '5-6', color: 'bg-amber-400', placeholder: 'e.g., Placed at least one order' },
                        { key: 'low', label: '3-4', color: 'bg-orange-400', placeholder: "e.g., Asked questions, showed interest but didn't buy" },
                        { key: 'minimal', label: '1-2', color: 'bg-gray-400', placeholder: 'e.g., Just browsing, no purchase intent' },
                      ].map(range => (
                        <div key={range.key} className="flex items-start gap-3">
                          <div className="flex items-center gap-1.5 pt-2 flex-shrink-0 w-16">
                            <span className={cn('w-2.5 h-2.5 rounded-full', range.color)} />
                            <span className="text-xs font-bold text-gray-700">{range.label}</span>
                          </div>
                          <input
                            type="text"
                            value={(configForm as any)[`lead_scoring_${range.key}`] || ''}
                            onChange={e => { setConfigForm(prev => ({ ...prev, [`lead_scoring_${range.key}`]: e.target.value })); setIsDirty(true); }}
                            placeholder={range.placeholder}
                            className="flex-1 px-3 py-2 text-xs border-2 border-gray-200 rounded-lg focus:border-rose-300 focus:outline-none transition-colors"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ── Advanced (collapsible) ── */}
            <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[2px_2px_0px_#1a1a2e] overflow-hidden hover:shadow-[3px_3px_0px_#1a1a2e] transition-all">
              <button onClick={() => setExpandedSection(expandedSection === 'advanced' ? null : 'advanced')} className="w-full p-4 flex items-start justify-between gap-3 text-left">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-4 h-4 text-gray-500" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900">Advanced</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Skills{agentSkills.length > 0 ? ` (${agentSkills.length})` : ''} · Greeting · Tone
                    </p>
                  </div>
                </div>
                <ChevronDown className={cn('w-4 h-4 text-gray-400 transition-transform flex-shrink-0 mt-1', expandedSection === 'advanced' && 'rotate-180')} />
              </button>
              {expandedSection === 'advanced' && (
                <div className="border-t border-gray-100 px-4 py-4 space-y-5">
                  {/* Skills */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Skills</span>
                      <div className="flex gap-2">
                        <button onClick={() => setActivePopup('link-skill')} className="text-xs text-violet-600 hover:text-violet-700 font-medium flex items-center gap-1">
                          <Plus className="w-3 h-3" /> Link
                        </button>
                        <button onClick={() => setActivePopup('create-skill')} className="text-xs text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1">
                          <Plus className="w-3 h-3" /> Create
                        </button>
                      </div>
                    </div>
                    {agentSkills.length === 0 ? (
                      <p className="text-xs text-gray-400">No skills linked</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {agentSkills.map(skill => (
                          <span key={skill.id} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 text-amber-800 text-xs font-medium border border-amber-200">
                            {skill.name}
                            {skill.skill_type === 'topic' && (
                              <button
                                onClick={async () => {
                                  try {
                                    await skillsService.unlinkAgent(skill.id, id!);
                                    setAgentSkills(prev => prev.filter(s => s.id !== skill.id));
                                    toast.success('Skill unlinked');
                                  } catch {
                                    toast.error('Failed to unlink skill');
                                  }
                                }}
                                className="ml-0.5 hover:text-red-600 transition-colors"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            )}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Tone */}
                  <div>
                    <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2 flex items-center gap-1">Tone <InfoTooltip content="Sets the overall communication style. Professional is formal and business-like, Friendly is warm and approachable, Casual is relaxed and conversational." /></span>
                    <div className="flex gap-2">
                      {(['professional', 'friendly', 'casual'] as const).map(t => (
                        <button
                          key={t}
                          onClick={() => { setConfigForm(prev => ({ ...prev, tone: t })); setIsDirty(true); }}
                          className={cn(
                            'px-3 py-1.5 rounded-lg text-xs font-medium border-2 transition-all capitalize',
                            configForm.tone === t
                              ? 'border-violet-500 bg-violet-50 text-violet-700'
                              : 'border-gray-150 text-gray-500 hover:border-gray-300',
                          )}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Greeting & Goodbye */}
                  <div className="space-y-3">
                    <ReadOnlyField
                      label="Greeting Message"
                      value={configForm.greeting_message}
                      onEdit={() => setActivePopup('greeting')}
                    />
                    <ReadOnlyField
                      label="Goodbye Message"
                      value={configForm.goodbye_message}
                      onEdit={() => setActivePopup('goodbye')}
                    />
                  </div>
                </div>
              )}
            </div>

          </div>

        </div>

        {/* --- Sticky save bar --- */}
        {isDirty && (
          <div className="sticky bottom-0 z-10 bg-white border-t-2 border-[#1a1a2e] px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-amber-400" />
                <span className="text-sm text-gray-600">Unsaved changes</span>
              </div>
              <div className="flex gap-2">
                <button onClick={handleDiscard} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                  Discard
                </button>
                <button
                  onClick={handleSaveConfiguration}
                  disabled={isSaving}
                  className="px-4 py-2 text-sm font-medium text-white bg-violet-500 hover:bg-violet-600 border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] rounded-xl transition-colors disabled:opacity-50"
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ======================= RIGHT: Test Chat ======================= */}
      <div className="w-full lg:w-[400px] xl:w-[440px] flex-shrink-0 border-t-2 lg:border-t-0 lg:border-l-2 border-[#1a1a2e] bg-gray-50 flex flex-col min-h-[400px] lg:min-h-0">
        <AgentTestChat agentId={id!} />
      </div>

      {/* ======================= MODALS / POPUPS ======================= */}

      {/* Profile Edit Popup */}
      <Modal isOpen={activePopup === 'profile'} onClose={() => setActivePopup(null)} title="Edit Profile" size="lg">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              value={configForm.name}
              onChange={e => { setConfigForm(prev => ({ ...prev, name: e.target.value })); setIsDirty(true); }}
              className={INPUT_CLASS}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={configForm.description}
              onChange={e => { setConfigForm(prev => ({ ...prev, description: e.target.value })); setIsDirty(true); }}
              className={TEXTAREA_CLASS}
              rows={5}
            />
          </div>
        </div>
        <div className="flex justify-end mt-6">
          <button onClick={() => setActivePopup(null)} className="px-4 py-2 text-sm font-medium text-white bg-violet-500 hover:bg-violet-600 border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] rounded-xl">Done</button>
        </div>
      </Modal>

      {/* Greeting Message Popup */}
      <Modal isOpen={activePopup === 'greeting'} onClose={() => setActivePopup(null)} title="Edit Greeting Message" size="lg">
        <MarkdownEditor
          value={configForm.greeting_message}
          onChange={v => { setConfigForm(prev => ({ ...prev, greeting_message: v })); setIsDirty(true); }}
          rows={6}
          placeholder="What should the agent say first?"
          tooltip="The first message your agent sends when a new conversation starts."
        />
        <div className="flex justify-end mt-4">
          <button onClick={() => setActivePopup(null)} className="px-4 py-2 text-sm font-medium text-white bg-violet-500 hover:bg-violet-600 border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] rounded-xl">Done</button>
        </div>
      </Modal>

      {/* Goodbye Message Popup */}
      <Modal isOpen={activePopup === 'goodbye'} onClose={() => setActivePopup(null)} title="Edit Goodbye Message" size="lg">
        <MarkdownEditor
          value={configForm.goodbye_message}
          onChange={v => { setConfigForm(prev => ({ ...prev, goodbye_message: v })); setIsDirty(true); }}
          rows={6}
          placeholder="What should the agent say when ending the conversation?"
          tooltip="The message your agent sends when ending a conversation."
        />
        <div className="flex justify-end mt-4">
          <button onClick={() => setActivePopup(null)} className="px-4 py-2 text-sm font-medium text-white bg-violet-500 hover:bg-violet-600 border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] rounded-xl">Done</button>
        </div>
      </Modal>

      {/* Custom Instructions Popup */}
      <Modal isOpen={activePopup === 'instructions'} onClose={() => setActivePopup(null)} title="Edit Custom Instructions" size="xl">
        <MarkdownEditor
          value={configForm.custom_instructions}
          onChange={v => { setConfigForm(prev => ({ ...prev, custom_instructions: v })); setIsDirty(true); }}
          rows={14}
          placeholder="Provide specific instructions for how the agent should behave..."
          tooltip="The core behavioral prompt for your agent. Write detailed instructions about how the agent should respond, what topics to cover, and what to avoid."
        />
        <div className="flex justify-end mt-4">
          <button onClick={() => setActivePopup(null)} className="px-4 py-2 text-sm font-medium text-white bg-violet-500 hover:bg-violet-600 border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] rounded-xl">Done</button>
        </div>
      </Modal>

      {/* Lead Criteria Popup */}
      <Modal isOpen={activePopup === 'lead-criteria'} onClose={() => setActivePopup(null)} title="Edit Lead Detection Criteria" size="lg">
        <MarkdownEditor
          value={configForm.lead_qualification_criteria}
          onChange={v => { setConfigForm(prev => ({ ...prev, lead_qualification_criteria: v })); setIsDirty(true); }}
          rows={6}
          placeholder="Describe your ideal high-priority customer..."
          tooltip="Describe your ideal customer. The AI uses this to score and flag high-potential leads."
        />
        <div className="flex justify-end mt-4">
          <button onClick={() => setActivePopup(null)} className="px-4 py-2 text-sm font-medium text-white bg-violet-500 hover:bg-violet-600 border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] rounded-xl">Done</button>
        </div>
      </Modal>

      {/* Knowledge Popup */}
      <Modal isOpen={activePopup === 'knowledge'} onClose={() => setActivePopup(null)} title="Add Knowledge" size="lg">
        {/* Tab switcher */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5 mb-5">
          <button
            onClick={() => setKnowledgeTab('browse')}
            className={cn(
              'flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all',
              knowledgeTab === 'browse' ? 'bg-white text-gray-900 shadow-[3px_3px_0px_#1a1a2e]' : 'text-gray-500 hover:text-gray-700',
            )}
          >
            <FolderOpen className="w-3.5 h-3.5" />
            Browse Knowledge Bases
          </button>
          <button
            onClick={() => setKnowledgeTab('upload')}
            className={cn(
              'flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all',
              knowledgeTab === 'upload' ? 'bg-white text-gray-900 shadow-[3px_3px_0px_#1a1a2e]' : 'text-gray-500 hover:text-gray-700',
            )}
          >
            <Upload className="w-3.5 h-3.5" />
            Upload Files
          </button>
        </div>

        {knowledgeTab === 'browse' ? (
          <div>
            <p className="text-xs text-gray-400 mb-4">
              Link whole knowledge bases to give this agent access to all their documents.
              Click to expand and preview documents inside.
            </p>
            {isLoadingKbs ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            ) : allKnowledgeBases.length === 0 ? (
              <div className="text-center py-8">
                <Database className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                <p className="text-sm text-gray-400">No knowledge bases in your workspace</p>
                <p className="text-xs text-gray-300 mt-1">Create one from the Knowledge page first</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {allKnowledgeBases.map(kb => {
                  const isLinked = linkedKbIds.has(kb.id);
                  const isExpanded = expandedKbId === kb.id;
                  const docs = kbDocuments[kb.id] || [];
                  const isLoadingDocs = loadingKbDocs === kb.id;
                  const isToggling = togglingKbId === kb.id;

                  return (
                    <div key={kb.id} className={cn(
                      'rounded-xl border-2 overflow-hidden transition-all',
                      isLinked ? 'border-emerald-300 bg-emerald-50/50' : 'border-[#1a1a2e]',
                    )}>
                      {/* KB header row */}
                      <div className="flex items-center gap-3 p-3">
                        <button
                          onClick={() => handleExpandKb(kb.id)}
                          className="p-1 rounded hover:bg-gray-100 transition-colors"
                        >
                          <ChevronDown className={cn(
                            'w-3.5 h-3.5 text-gray-400 transition-transform',
                            isExpanded && 'rotate-180',
                          )} />
                        </button>

                        <div className={cn(
                          'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
                          isLinked ? 'bg-emerald-100' : 'bg-gray-100',
                        )}>
                          <Database className={cn('w-4 h-4', isLinked ? 'text-emerald-600' : 'text-gray-400')} />
                        </div>

                        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => handleExpandKb(kb.id)}>
                          <p className="text-sm font-medium text-gray-900 truncate">{kb.name}</p>
                          <p className="text-[11px] text-gray-400">
                            {kb.document_count ?? 0} document{(kb.document_count ?? 0) !== 1 ? 's' : ''}
                            {kb.source?.type && kb.source.type !== 'manual' && (
                              <span className="ml-1.5 text-gray-300">
                                &middot; {kb.source.integration_name || kb.source.type}
                              </span>
                            )}
                          </p>
                        </div>

                        <button
                          onClick={() => handleToggleKb(kb.id)}
                          disabled={!!isToggling}
                          className={cn(
                            'px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex-shrink-0',
                            isLinked
                              ? 'bg-emerald-100 text-emerald-700 hover:bg-red-50 hover:text-red-600'
                              : 'bg-violet-50 text-violet-600 hover:bg-violet-100',
                            isToggling && 'opacity-50',
                          )}
                        >
                          {isToggling ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : isLinked ? (
                            'Linked'
                          ) : (
                            '+ Link'
                          )}
                        </button>
                      </div>

                      {/* Expanded document list (read-only preview) */}
                      {isExpanded && (
                        <div className="border-t-2 border-[#1a1a2e] bg-white px-3 py-2">
                          {isLoadingDocs ? (
                            <div className="flex items-center justify-center py-4">
                              <Loader2 className="w-4 h-4 animate-spin text-gray-300" />
                            </div>
                          ) : docs.length === 0 ? (
                            <p className="text-xs text-gray-300 text-center py-3">No documents in this knowledge base</p>
                          ) : (
                            <div className="space-y-0.5">
                              {isLinked && (
                                <p className="text-[10px] text-emerald-600 px-2 py-1 mb-1">
                                  All documents included via linked knowledge base
                                </p>
                              )}
                              {docs.map(doc => (
                                <div
                                  key={doc.id}
                                  className="w-full flex items-center gap-2.5 py-2 px-2 rounded-lg text-left"
                                >
                                  <FileText className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" />
                                  <span className="text-xs text-gray-600 flex-1 truncate">{doc.name}</span>
                                  <span className="text-[10px] text-gray-300 flex-shrink-0">{doc.file_type || 'text'}</span>
                                  <span className={cn(
                                    'text-[10px] font-medium flex-shrink-0',
                                    doc.status === 'processed' ? 'text-emerald-500' :
                                    doc.status === 'processing' ? 'text-amber-500' : 'text-gray-300',
                                  )}>
                                    {doc.status}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div>
            <p className="text-xs text-gray-400 mb-4">
              Upload files directly to this agent's personal knowledge base.
            </p>
            <FileUploadZone pendingFiles={kbPendingFiles} onFilesChange={setKbPendingFiles} />
            {kbPendingFiles.length > 0 && (
              <button
                onClick={handleUploadKbFiles}
                disabled={isUploadingKb}
                className="mt-3 px-4 py-2 text-sm font-medium text-white bg-violet-500 hover:bg-violet-600 border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] rounded-xl disabled:opacity-50"
              >
                {isUploadingKb ? 'Uploading...' : `Upload ${kbPendingFiles.length} file(s)`}
              </button>
            )}
          </div>
        )}

        <div className="flex justify-end mt-6">
          <button onClick={() => setActivePopup(null)} className="px-4 py-2 text-sm font-medium text-white bg-violet-500 hover:bg-violet-600 border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] rounded-xl">Done</button>
        </div>
      </Modal>

      {/* Channels Popup */}
      <Modal isOpen={activePopup === 'channels'} onClose={() => setActivePopup(null)} title="Connect Channels" size="md">
        <p className="text-sm text-gray-500 mb-4">Select channels to deploy this agent on</p>
        <div className="space-y-2">
          {orgChannels.map(ch => {
            const isConnected = ch.agents?.some(a => a.agent_id === id);
            return (
              <label
                key={ch.id}
                className={cn(
                  'flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all',
                  isConnected ? 'border-sky-400 bg-sky-50' : 'border-[#1a1a2e]',
                )}
              >
                <input
                  type="checkbox"
                  checked={!!isConnected}
                  onChange={async () => {
                    try {
                      if (isConnected) {
                        await channelsService.removeChannelAgent(ch.id, id!);
                      } else {
                        await channelsService.addChannelAgent(ch.id, id!);
                      }
                      const updated = await channelsService.getChannels();
                      setOrgChannels(updated);
                    } catch {
                      toast.error('Failed to update channel');
                    }
                  }}
                  className="sr-only"
                />
                <div className={cn('w-5 h-5 rounded border-2 flex items-center justify-center transition-colors', isConnected ? 'bg-sky-500 border-sky-500' : 'border-gray-300')}>
                  {isConnected && <CheckCircle className="w-3 h-3 text-white" />}
                </div>
                <span className="text-lg">{getChannelEmoji(ch.channel_type)}</span>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{ch.name}</p>
                  <p className="text-xs text-gray-400 capitalize">{ch.channel_type} &middot; {ch.status}</p>
                </div>
              </label>
            );
          })}
        </div>
        {orgChannels.length === 0 && (
          <p className="text-center text-sm text-gray-400 py-6">
            No channels set up yet.{' '}
            <button onClick={() => navigate('/channels')} className="text-violet-600 font-medium">Create one</button>
          </p>
        )}
        <div className="flex justify-end mt-6">
          <button onClick={() => setActivePopup(null)} className="px-4 py-2 text-sm font-medium text-white bg-violet-500 hover:bg-violet-600 border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] rounded-xl">Done</button>
        </div>
      </Modal>

      {/* Pipeline Add/Edit Step Popup */}
      <Modal
        isOpen={activePopup === 'pipeline-add'}
        onClose={() => { setActivePopup(null); resetPipelineForm(); }}
        title={editingPipelineTask ? 'Edit Step' : 'Add a Step'}
        size="lg"
      >
        {pipelineWizardStep === 'choose' ? (
          <div>
            <p className="text-sm text-gray-500 mb-5">What should the agent do?</p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { type: 'data_collection', icon: '\uD83D\uDCCB', title: 'Collect Info', desc: 'Gather information from the customer' },
                { type: 'document_creation', icon: '\uD83D\uDCDD', title: 'Generate Document', desc: 'Create a quote, summary, report' },
                { type: 'goal', icon: '\uD83C\uDFAF', title: 'Set a Goal', desc: 'Define what success looks like' },
                { type: 'action', icon: '\u26A1', title: 'Action', desc: 'Execute a workflow action' },
              ].map(opt => (
                <button
                  key={opt.type}
                  onClick={() => { setPipelineForm(prev => ({ ...prev, task_type: opt.type as AgentTaskType })); setPipelineWizardStep('configure'); }}
                  className="flex flex-col items-center gap-2 p-5 rounded-xl border-2 border-[#1a1a2e] hover:border-violet-300 hover:bg-violet-50/50 transition-all text-center"
                >
                  <span className="text-2xl">{opt.icon}</span>
                  <span className="text-sm font-semibold text-gray-900">{opt.title}</span>
                  <span className="text-xs text-gray-400">{opt.desc}</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Step name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name this step</label>
              <input
                value={pipelineForm.name}
                onChange={e => setPipelineForm(prev => ({ ...prev, name: e.target.value }))}
                className={INPUT_CLASS}
                placeholder={pipelineForm.task_type === 'action' ? 'e.g. Process Order, Book Appointment' : pipelineForm.task_type === 'data_collection' ? 'e.g. Collect Order Details' : pipelineForm.task_type === 'goal' ? 'e.g. Answer Customer Questions' : 'e.g. Generate Quote'}
              />
            </div>

            {/* Type-specific config */}
            {pipelineForm.task_type === 'data_collection' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">How should the agent collect this?</label>
                  <textarea
                    value={pipelineForm.description}
                    onChange={(e) => { setPipelineForm(prev => ({ ...prev, description: e.target.value })); setIsDirty(true); }}
                    placeholder="e.g., Ask politely for each detail one at a time. If they mention allergies or dietary restrictions, make a note."
                    rows={2}
                    className="w-full px-3 py-2 text-sm border-2 border-[#1a1a2e] rounded-lg resize-none"
                  />
                </div>
                <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">What info do you need?</label>
                {/* Suggested field buttons */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {SUGGESTED_FIELDS.map(f => {
                    const alreadyAdded = pipelineForm.fields.some(ef => ef.key === f.key);
                    return (
                      <button
                        key={f.key}
                        onClick={() => addSuggestedField(f)}
                        disabled={alreadyAdded}
                        className={cn(
                          'px-3 py-1.5 rounded-full text-xs font-medium border transition-all',
                          alreadyAdded
                            ? 'bg-gray-50 text-gray-300 border-[#1a1a2e] cursor-not-allowed'
                            : 'bg-white text-gray-600 border-[#1a1a2e] hover:border-violet-300 hover:text-violet-600',
                        )}
                      >
                        {alreadyAdded ? '\u2713' : '+'} {f.label}
                      </button>
                    );
                  })}
                </div>

                {/* Added fields list */}
                {pipelineForm.fields.length > 0 && (
                  <div className="space-y-2 mb-4">
                    {pipelineForm.fields.map((field, idx) => (
                      <div key={field.key} className="flex items-center gap-3 py-2 px-3 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-800 flex-1">{field.label}</span>
                        <span className="text-[11px] text-gray-400 bg-white px-2 py-0.5 rounded">{field.type}</span>
                        <button
                          onClick={() => {
                            setPipelineForm(prev => ({
                              ...prev,
                              fields: prev.fields.map((f, i) => i === idx ? { ...f, required: !f.required } : f),
                            }));
                          }}
                          className={cn(
                            'text-[11px] font-medium px-2 py-0.5 rounded',
                            field.required ? 'text-violet-700 bg-violet-100' : 'text-gray-400 bg-gray-100',
                          )}
                        >
                          {field.required ? 'Required' : 'Optional'}
                        </button>
                        <button
                          onClick={() => setPipelineForm(prev => ({ ...prev, fields: prev.fields.filter((_, i) => i !== idx) }))}
                          className="p-1 hover:bg-red-50 rounded"
                        >
                          <X className="w-3 h-3 text-red-400" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Custom field input */}
                <div className="flex gap-2">
                  <input
                    value={customFieldInput}
                    onChange={e => setCustomFieldInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomField(); } }}
                    className={cn(INPUT_CLASS, 'flex-1')}
                    placeholder='Type a field name, e.g. "company size"'
                  />
                  <button
                    onClick={addCustomField}
                    disabled={!customFieldInput.trim()}
                    className="px-4 py-2 text-sm font-medium text-violet-600 bg-violet-50 hover:bg-violet-100 rounded-lg disabled:opacity-40 transition-colors"
                  >
                    Add
                  </button>
                </div>
                </div>
              </div>
            )}

            {pipelineForm.task_type === 'document_creation' && (
              <div className="space-y-4">
                <MarkdownEditor
                  label="What should be created?"
                  value={pipelineForm.document_guidance}
                  onChange={v => setPipelineForm(prev => ({ ...prev, document_guidance: v }))}
                  rows={4}
                  placeholder="Describe in plain English what this document should contain..."
                  tooltip="Instructions for the AI on what document to generate from the collected data."
                />

                {/* Reference Template */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Reference Template
                    <span className="text-gray-400 font-normal ml-1">(optional)</span>
                  </label>
                  <select
                    value={pipelineForm.reference_template_id}
                    onChange={e => setPipelineForm(prev => ({ ...prev, reference_template_id: e.target.value }))}
                    className={cn(INPUT_CLASS)}
                  >
                    <option value="">None — generate freely</option>
                    {referenceTemplates.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-400 mt-1">
                    Select a reference document the AI will use as a structural guide.
                    {referenceTemplates.length === 0 && ' Upload templates via the Documents page.'}
                  </p>
                </div>

                {/* Output Name Template */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Output Name Template
                    <span className="text-gray-400 font-normal ml-1">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={pipelineForm.output_name_template}
                    onChange={e => setPipelineForm(prev => ({ ...prev, output_name_template: e.target.value }))}
                    className={cn(INPUT_CLASS)}
                    placeholder="e.g., Insurance Quote — {{customer_name}}"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Name pattern for generated documents. Use {'{{field_name}}'} placeholders for collected data.
                  </p>
                </div>
              </div>
            )}

            {pipelineForm.task_type === 'goal' && (
              <MarkdownEditor
                label="What does success look like?"
                value={pipelineForm.description}
                onChange={v => setPipelineForm(prev => ({ ...prev, description: v }))}
                rows={4}
                placeholder="Describe the goal criteria..."
                tooltip="Describe the behavioral objective the AI should pursue during conversations."
              />
            )}

            {pipelineForm.task_type === 'action' && (
              <div className="space-y-4">
                {/* Description — tells the AI when to trigger this action */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">When should this happen?</label>
                  <textarea
                    value={pipelineForm.description}
                    onChange={(e) => { setPipelineForm(prev => ({ ...prev, description: e.target.value })); setIsDirty(true); }}
                    placeholder="e.g., When the customer has confirmed their order items, delivery address and phone number, submit the order."
                    rows={2}
                    className="w-full px-3 py-2 text-sm border-2 border-[#1a1a2e] rounded-lg resize-none"
                  />
                </div>

                {/* Action type selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">What type of action?</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setPipelineForm(prev => ({ ...prev, action_type: 'submission' }))}
                      className={cn(
                        'p-3 rounded-lg border-2 text-left transition-all',
                        pipelineForm.action_type === 'submission'
                          ? 'border-violet-500 bg-violet-50'
                          : 'border-gray-200 hover:border-gray-300'
                      )}
                    >
                      <p className="text-sm font-semibold text-gray-800">Request / Booking</p>
                      <p className="text-[10px] text-gray-500 mt-0.5">Appointments, bookings, leads, inquiries</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPipelineForm(prev => ({ ...prev, action_type: 'order' }))}
                      className={cn(
                        'p-3 rounded-lg border-2 text-left transition-all',
                        pipelineForm.action_type === 'order'
                          ? 'border-violet-500 bg-violet-50'
                          : 'border-gray-200 hover:border-gray-300'
                      )}
                    >
                      <p className="text-sm font-semibold text-gray-800">Order</p>
                      <p className="text-[10px] text-gray-500 mt-0.5">Items with quantities and prices</p>
                    </button>
                  </div>
                </div>

                {/* Always saved info */}
                <div className="bg-blue-50 rounded-lg p-3 flex items-start gap-2.5">
                  <CheckCircle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-blue-800">Data is always saved to ChatCup</p>
                    <p className="text-[10px] text-blue-600">Every submission is saved and visible in your Orders page.</p>
                  </div>
                </div>

                {/* Store push + notifications are automatic — no config needed */}
                <div className="bg-emerald-50 rounded-lg p-3 flex items-start gap-2.5">
                  <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-emerald-800">Your team is notified automatically</p>
                    <p className="text-[10px] text-emerald-600">Email + in-app notification sent to all admins when an order comes in. If you have a connected store, orders are pushed there automatically.</p>
                  </div>
                </div>


                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={pipelineForm.require_confirmation}
                    onChange={(e) => setPipelineForm(prev => ({ ...prev, require_confirmation: e.target.checked }))}
                    className="rounded border-gray-300"
                  />
                  Ask customer to confirm before executing
                </label>
              </div>
            )}

            {/* Run order */}
            {pipelineTasks.filter(t => t.id !== editingPipelineTask?.id).length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Run after:</label>
                <select
                  value={pipelineForm.depends_on}
                  onChange={e => { setPipelineForm(prev => ({ ...prev, depends_on: e.target.value })); setIsDirty(true); }}
                  className="w-full h-10 px-3 border-2 border-[#1a1a2e] rounded-lg text-sm bg-white"
                >
                  <option value="">Any time (no specific order)</option>
                  {pipelineTasks.filter(t => t.id !== editingPipelineTask?.id).map(t => (
                    <option key={t.id} value={t.id}>After: {t.name}</option>
                  ))}
                </select>
                <p className="text-[10px] text-gray-400 mt-1">Choose if this step should wait for another step to complete first.</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-between pt-4 border-t-2 border-[#1a1a2e]">
              {!editingPipelineTask ? (
                <button onClick={() => setPipelineWizardStep('choose')} className="text-sm text-gray-500 hover:text-gray-700">
                  &larr; Back
                </button>
              ) : (
                <div />
              )}
              <div className="flex gap-2">
                <button onClick={() => { setActivePopup(null); resetPipelineForm(); }} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
                <button onClick={handleSavePipelineStep} className="px-4 py-2 text-sm font-medium text-white bg-violet-500 hover:bg-violet-600 border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] rounded-xl">
                  {editingPipelineTask ? 'Update Step' : 'Add Step'}
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Escalation Rule Popup */}
      <Modal isOpen={activePopup === 'escalation'} onClose={() => setActivePopup(null)} title={editingEscalation ? 'Edit Escalation Rule' : 'Add Escalation Rule'} size="lg">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rule Name</label>
            <input
              value={escalationForm.name}
              onChange={e => setEscalationForm(prev => ({ ...prev, name: e.target.value }))}
              className={INPUT_CLASS}
              placeholder="e.g., Frustrated Customer Escalation"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
            <select
              value={escalationForm.priority}
              onChange={e => setEscalationForm(prev => ({ ...prev, priority: e.target.value as 'low' | 'normal' | 'high' | 'urgent' }))}
              className={INPUT_CLASS}
            >
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
          <div className="p-4 bg-violet-50 rounded-lg border border-violet-200">
            <label className="block text-sm font-medium text-violet-900 mb-1.5">AI Evaluation Guideline</label>
            <MarkdownEditor
              value={escalationForm.guideline}
              onChange={v => setEscalationForm(prev => ({ ...prev, guideline: v }))}
              rows={5}
              placeholder="e.g., Escalate when the customer expresses frustration or asks to speak to a manager."
              tooltip="Describe in plain language when this escalation rule should trigger. The AI evaluates this during every conversation."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notification Message</label>
            <textarea
              value={escalationForm.notification_message}
              onChange={e => setEscalationForm(prev => ({ ...prev, notification_message: e.target.value }))}
              className={TEXTAREA_CLASS}
              rows={5}
              placeholder="Urgent: {{customer_name}} needs attention. Reason: {{escalation_reason}}"
            />
            <p className="text-xs text-gray-400 mt-1">Supports: {'{{customer_name}}'}, {'{{channel}}'}, {'{{conversation_id}}'}</p>
          </div>
          {orgNotifChannels.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Notify Via</label>
              <div className="flex flex-wrap gap-2">
                {orgNotifChannels.map(channel => (
                  <label
                    key={channel.id}
                    className={cn(
                      'flex items-center gap-2 px-4 py-2 rounded-lg border-2 cursor-pointer transition-all',
                      escalationForm.notification_channel_ids.includes(channel.id)
                        ? 'border-violet-500 bg-violet-50 text-violet-700'
                        : 'border-[#1a1a2e]',
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={escalationForm.notification_channel_ids.includes(channel.id)}
                      onChange={() => toggleNotifChannelId(channel.id)}
                      className="sr-only"
                    />
                    <span className="font-medium text-sm">{channel.name}</span>
                    <span className="text-xs text-gray-400">({channel.type})</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-4 border-t-2 border-[#1a1a2e]">
            <button onClick={() => setActivePopup(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
            <button onClick={handleSaveEscalation} className="px-4 py-2 text-sm font-medium text-white bg-violet-500 hover:bg-violet-600 border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] rounded-xl">
              {editingEscalation ? 'Update Rule' : 'Add Rule'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Link Skill Popup */}
      <Modal isOpen={activePopup === 'link-skill'} onClose={() => setActivePopup(null)} title="Link Skills" size="md">
        <div className="space-y-2">
          {allSkills.filter(s => s.skill_type === 'topic' && !agentSkills.some(as => as.id === s.id)).map(skill => (
            <button
              key={skill.id}
              onClick={async () => {
                try {
                  await skillsService.linkAgents(skill.id, [id!]);
                  const updated = await skillsService.getAgentSkills(id!);
                  setAgentSkills(updated);
                  toast.success('Skill linked');
                } catch {
                  toast.error('Failed to link skill');
                }
              }}
              className="w-full flex items-center justify-between p-3 rounded-xl border-2 border-[#1a1a2e] hover:bg-violet-50/50 transition-all"
            >
              <div className="text-left">
                <p className="text-sm font-medium text-gray-900">{skill.name}</p>
                <p className="text-xs text-gray-400">{skill.rules.length} rules</p>
              </div>
              <Plus className="w-4 h-4 text-gray-400" />
            </button>
          ))}
          {allSkills.filter(s => s.skill_type === 'topic' && !agentSkills.some(as => as.id === s.id)).length === 0 && (
            <p className="text-center text-sm text-gray-400 py-6">No available skills to link</p>
          )}
        </div>
        <div className="flex justify-end mt-6">
          <button onClick={() => setActivePopup(null)} className="px-4 py-2 text-sm font-medium text-white bg-violet-500 hover:bg-violet-600 border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] rounded-xl">Done</button>
        </div>
      </Modal>

      {/* Create Skill Popup */}
      <Modal isOpen={activePopup === 'create-skill'} onClose={() => setActivePopup(null)} title="Create New Skill" size="lg">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Skill Name</label>
            <input
              value={newSkillForm.name}
              onChange={e => setNewSkillForm(prev => ({ ...prev, name: e.target.value }))}
              className={INPUT_CLASS}
              placeholder="e.g. Pricing FAQ"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input
              value={newSkillForm.description}
              onChange={e => setNewSkillForm(prev => ({ ...prev, description: e.target.value }))}
              className={INPUT_CLASS}
              placeholder="What does this skill teach the agent?"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <div className="flex gap-2">
              {(['topic', 'common'] as const).map(t => (
                <button
                  key={t}
                  onClick={() => setNewSkillForm(prev => ({ ...prev, skill_type: t }))}
                  className={cn(
                    'px-4 py-2 rounded-lg text-sm font-medium border-2 transition-all capitalize',
                    newSkillForm.skill_type === t
                      ? 'border-violet-400 bg-violet-50 text-violet-700'
                      : 'border-[#1a1a2e] text-gray-500',
                  )}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Rules</label>
            <div className="space-y-2">
              {newSkillForm.rules.map((rule, idx) => (
                <div key={idx} className="flex gap-2">
                  <input
                    value={rule}
                    onChange={e => {
                      const r = [...newSkillForm.rules];
                      r[idx] = e.target.value;
                      setNewSkillForm(prev => ({ ...prev, rules: r }));
                    }}
                    className={cn(INPUT_CLASS, 'flex-1')}
                    placeholder="e.g. Always mention our money-back guarantee"
                  />
                  {newSkillForm.rules.length > 1 && (
                    <button
                      onClick={() => setNewSkillForm(prev => ({ ...prev, rules: prev.rules.filter((_, i) => i !== idx) }))}
                      className="p-2 hover:bg-red-50 rounded-lg"
                    >
                      <X className="w-4 h-4 text-red-400" />
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={() => setNewSkillForm(prev => ({ ...prev, rules: [...prev.rules, ''] }))}
                className="text-xs text-violet-600 font-medium"
              >
                + Add rule
              </button>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={() => setActivePopup(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
          <button
            onClick={async () => {
              if (!newSkillForm.name.trim()) { toast.error('Name required'); return; }
              try {
                const created = await skillsService.createSkill({
                  name: newSkillForm.name,
                  description: newSkillForm.description,
                  skill_type: newSkillForm.skill_type,
                  rules: newSkillForm.rules.filter(r => r.trim()),
                  few_shot_examples: newSkillForm.examples,
                });
                await skillsService.linkAgents(created.id, [id!]);
                const updatedSkills = await skillsService.getAgentSkills(id!);
                setAgentSkills(updatedSkills);
                setAllSkills(prev => [...prev, created]);
                setNewSkillForm({ name: '', description: '', skill_type: 'topic', rules: [''], examples: [] });
                setActivePopup(null);
                toast.success('Skill created & linked');
              } catch {
                toast.error('Failed to create skill');
              }
            }}
            className="px-4 py-2 text-sm font-medium text-white bg-violet-500 hover:bg-violet-600 border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] rounded-xl"
          >
            Create & Link
          </button>
        </div>
      </Modal>
    </div>
  );
};
